import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CURRENCY_OPTIONS, formatPrice } from "@/lib/currency";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { Upload, X, Sparkles, Home, Calendar, MapPin, ImagePlus, Wifi } from "lucide-react";
import { fileUrl } from "@/lib/api";

const AMENITIES = ["Wifi", "Laundry", "Kitchen", "Heating", "AC", "Balcony", "Parking", "Bike"];

export default function CreateListing() {
    const navigate = useNavigate();
    const { setUser } = useAuth();
    const [form, setForm] = useState({
        title: "", city: "", country: "", university: "",
        description: "",
        rent_per_month: "",
        currency: "EUR",
        available_from: "", available_to: "",
        destination_city: "", destination_country: "",
        amenities: [],
        photos: [],
    });
    const [submitting, setSubmitting] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [citySuggestions, setCitySuggestions] = useState([]);

    useEffect(() => {
        api.get("/cities/suggest")
            .then(({ data }) => setCitySuggestions(data.cities || []))
            .catch(() => { /* fallback to no suggestions */ });
    }, []);

    const f = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    const toggleAmenity = (a) => {
        setForm((p) => ({
            ...p,
            amenities: p.amenities.includes(a) ? p.amenities.filter((x) => x !== a) : [...p.amenities, a],
        }));
    };

    const onUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true);
        try {
            const uploaded = [];
            for (const file of files) {
                const fd = new FormData();
                fd.append("file", file);
                const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                uploaded.push(data.path);
            }
            setForm((p) => ({ ...p, photos: [...p.photos, ...uploaded] }));
            toast.success(`${uploaded.length} photo(s) uploaded`);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const removePhoto = (idx) => {
        setForm((p) => ({ ...p, photos: p.photos.filter((_, i) => i !== idx) }));
    };

    const submit = async (e) => {
        e.preventDefault();
        if (form.photos.length === 0) {
            toast.error("Please upload at least one photo before publishing.");
            return;
        }
        setSubmitting(true);
        try {
            const payload = { ...form, rent_per_month: parseFloat(form.rent_per_month) };
            const { data } = await api.post("/rooms", payload);
            // Refresh auth state so Browse unlocks immediately.
            try {
                const me = await api.get("/auth/me");
                setUser(me.data);
            } catch { /* ignore */ }
            toast.success("Room listed!");
            navigate(`/rooms/${data.id}`);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="create-listing-page" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-16 sm:py-10">
            {/* Hero card — same fresh coral palette as the other app pages */}
            <motion.section
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="relative overflow-hidden rounded-3xl p-6 sm:p-8 text-white shadow-xl"
                style={{ background: "linear-gradient(135deg, #f4a261 0%, #ee8f6d 45%, #dd6b4d 100%)" }}
            >
                <div className="absolute -right-10 -top-10 w-56 h-56 rounded-full bg-white/20 blur-2xl" aria-hidden />
                <div className="absolute -left-16 -bottom-16 w-56 h-56 rounded-full bg-white/10 blur-2xl" aria-hidden />
                <div className="relative">
                    <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-white/80 inline-flex items-center gap-2">
                        <Sparkles className="w-3.5 h-3.5" /> New listing
                    </div>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold mt-2 leading-tight">List your room</h1>
                    <p className="text-white/85 mt-2 text-sm sm:text-base max-w-lg">
                        The more precise you are, the better the matches. Takes about 2 minutes.
                    </p>
                </div>
            </motion.section>

            <form onSubmit={submit} className="mt-6 space-y-4 sm:space-y-6">
                <FormCard title="About your room" icon={Home} delay={0.05}>
                    <div>
                        <Label>Title</Label>
                        <Input required data-testid="listing-title-input" className="mt-2 rounded-xl h-11" value={form.title} onChange={f("title")} placeholder="Cozy studio in city center" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>City</Label>
                            <Input required list="city-suggestions" data-testid="listing-city-input" className="mt-2 rounded-xl h-11" value={form.city} onChange={f("city")} placeholder="Start typing..." />
                            <p className="text-xs text-muted-foreground mt-1.5">Please use the international (English) name. E.g. <span className="font-medium text-foreground">Rome</span> instead of Roma, <span className="font-medium text-foreground">Prague</span> instead of Praag.</p>
                        </div>
                        <div>
                            <Label>Country</Label>
                            <Input required data-testid="listing-country-input" className="mt-2 rounded-xl h-11" value={form.country} onChange={f("country")} />
                        </div>
                    </div>
                    <div>
                        <Label>University</Label>
                        <Input data-testid="listing-university-input" className="mt-2 rounded-xl h-11" value={form.university} onChange={f("university")} placeholder="e.g. Sapienza, KU Leuven…" />
                    </div>
                    <div>
                        <Label>Description</Label>
                        <Textarea required data-testid="listing-description-input" className="mt-2 rounded-xl min-h-28" value={form.description} onChange={f("description")} placeholder="Tell us about the vibe, neighbors, neighborhood..." />
                    </div>
                </FormCard>

                <FormCard title="Dates and price" icon={Calendar} delay={0.1}>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Available from</Label>
                            <Input type="date" required data-testid="listing-from-input" className="mt-2 rounded-xl h-11" value={form.available_from} onChange={f("available_from")} />
                        </div>
                        <div>
                            <Label>Available until</Label>
                            <Input type="date" required data-testid="listing-to-input" className="mt-2 rounded-xl h-11" value={form.available_to} onChange={f("available_to")} />
                        </div>
                    </div>
                    <div>
                        <Label>Rent per month</Label>
                        <div className="mt-2 grid grid-cols-[1fr_140px] sm:grid-cols-[1fr_180px] gap-2">
                            <Input
                                type="number" min="0" step="0.01" required
                                data-testid="listing-rent-input"
                                className="rounded-xl h-11"
                                placeholder="e.g. 550"
                                value={form.rent_per_month}
                                onChange={f("rent_per_month")}
                            />
                            <Select
                                value={form.currency}
                                onValueChange={(v) => setForm((p) => ({ ...p, currency: v }))}
                            >
                                <SelectTrigger data-testid="listing-currency-select" className="rounded-xl h-11">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="max-h-[300px]">
                                    {CURRENCY_OPTIONS.map((c) => (
                                        <SelectItem key={c.code} value={c.code} data-testid={`currency-option-${c.code}`}>
                                            {c.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {form.rent_per_month && (
                            <p className="text-xs text-muted-foreground mt-2" data-testid="listing-rent-preview">
                                Listed as <span className="font-medium text-foreground">{formatPrice(form.rent_per_month, form.currency, { perMonth: true })}</span>
                            </p>
                        )}
                    </div>
                </FormCard>

                <FormCard title="Where are you heading?" icon={MapPin} delay={0.15}>
                    <p className="text-sm text-muted-foreground -mt-2">We match you with someone coming to your city and leaving from your destination.</p>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Destination city</Label>
                            <Input list="city-suggestions" data-testid="listing-dest-city-input" className="mt-2 rounded-xl h-11" value={form.destination_city} onChange={f("destination_city")} placeholder="Start typing..." />
                            <p className="text-xs text-muted-foreground mt-1.5">Use the international (English) name.</p>
                        </div>
                        <div>
                            <Label>Destination country</Label>
                            <Input data-testid="listing-dest-country-input" className="mt-2 rounded-xl h-11" value={form.destination_country} onChange={f("destination_country")} />
                        </div>
                    </div>
                </FormCard>

                <FormCard title="Amenities" icon={Wifi} delay={0.2}>
                    <div className="flex flex-wrap gap-2">
                        {AMENITIES.map((a) => (
                            <button
                                key={a}
                                type="button"
                                data-testid={`amenity-${a}`}
                                onClick={() => toggleAmenity(a)}
                                className={`px-4 py-2 rounded-full text-sm transition-all duration-200 border ${
                                    form.amenities.includes(a)
                                        ? "bg-secondary text-secondary-foreground border-secondary"
                                        : "bg-white text-foreground border-border hover:bg-muted"
                                }`}
                            >
                                {a}
                            </button>
                        ))}
                    </div>
                </FormCard>

                <FormCard title="Photos" icon={ImagePlus} delay={0.25} rightSlot={<span className="text-xs font-medium text-muted-foreground">At least 1 required</span>}>
                    <label className="flex flex-col items-center justify-center border-2 border-dashed border-border rounded-2xl p-8 cursor-pointer hover:bg-muted transition-colors" data-testid="photo-upload-label">
                        <Upload className="w-8 h-8 text-muted-foreground" />
                        <span className="mt-2 text-sm text-muted-foreground">{uploading ? "Uploading…" : "Click or drag to upload photos"}</span>
                        <input type="file" accept="image/*" multiple className="hidden" onChange={onUpload} disabled={uploading} data-testid="photo-upload-input" />
                    </label>
                    <p className="text-xs text-muted-foreground italic leading-relaxed" data-testid="photo-hint-text">
                        No good picture of the room yet? Upload your favourite photo of the city instead ✨ — you can always replace it later from the room edit page.
                    </p>
                    {form.photos.length > 0 && (
                        <div className="grid grid-cols-3 gap-3">
                            {form.photos.map((p, i) => (
                                <div key={i} className="relative group" data-testid={`photo-preview-${i}`}>
                                    <img loading="lazy" decoding="async" src={fileUrl(p)} alt="" className="w-full aspect-square object-cover rounded-xl" />
                                    <button type="button" onClick={() => removePhoto(i)} className="absolute top-1 right-1 w-7 h-7 bg-foreground text-background rounded-full flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity" data-testid={`photo-remove-${i}`}>
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </FormCard>

                <Button type="submit" disabled={submitting} data-testid="listing-submit-btn"
                    className="w-full rounded-full h-12 bg-primary hover:bg-primary/90 text-base">
                    {submitting ? "Publishing…" : "Publish room"}
                </Button>

                <datalist id="city-suggestions">
                    {citySuggestions.map((c) => <option key={c} value={c} />)}
                </datalist>
            </form>
        </div>
    );
}

function FormCard({ title, icon: Icon, delay = 0, rightSlot, children }) {
    return (
        <motion.section
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ delay, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="bg-white border border-border/60 rounded-3xl p-5 sm:p-6"
        >
            <div className="flex items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Icon className="w-4 h-4 text-primary" />
                    </div>
                    <span className="font-display text-base font-semibold">{title}</span>
                </div>
                {rightSlot}
            </div>
            <div className="space-y-4">{children}</div>
        </motion.section>
    );
}
